/**
 * copyOverrides: admin-edited app copy, applied to the live DOM.
 *
 * WHY THIS SHAPE (owner direction, 2026-09-11)
 *   The owner wants to change or remove any piece of text anywhere in the app
 *   and have it reach every device without a build. Instrumenting every string
 *   with an id would take months and still miss the next page. So this works
 *   the other way round: an override is keyed by the ROUTE and the ORIGINAL
 *   text, and a MutationObserver swaps matching text nodes as React renders
 *   them. Nothing on a page has to know it exists.
 *
 * KEYS
 *   route:    the hash path with numeric segments collapsed to ":id"
 *             ("/pokemon/25" -> "/pokemon/:id"), or "*" for every page.
 *   original: the bundled text of ONE text node, whitespace-collapsed and
 *             trimmed. `normalizeCopyText` must agree with the Worker's copy
 *             (cloudflare/src/routes/copyOverrides.ts) or a key written from a
 *             live node stops matching after a remount.
 *
 * ACTIONS
 *   replace      the text node takes `value` (leading/trailing whitespace of
 *                the original is kept so "Hello " + <b> still spaces).
 *   hide         the element holding the text is hidden ([data-nx-copy-hidden]).
 *   hide_section the nearest card/section around it is hidden.
 *
 * REACT SAFETY
 *   React only writes a text node when its prop changes, so a swapped value
 *   survives re-renders; when React does write (a real prop change) the
 *   observer sees the new text, treats it as the new original, and matches
 *   again. Hidden elements carry an attribute React never manages, so it is
 *   never stripped. The engine never touches nodes inside the editor sheet
 *   ([data-nx-copy-editor]) or form controls.
 *
 * COST
 *   Walks only added subtrees (and one full pass on route change / data
 *   change), and only when an override exists for the route or "*". With no
 *   overrides for a page the observer callback returns immediately.
 */

export type CopyAction = "replace" | "hide" | "hide_section";

export interface CopyOverride {
  route: string;
  original: string;
  action: CopyAction;
  value: string | null;
}

export const COPY_HIDDEN_ATTR = "data-nx-copy-hidden";
export const COPY_EDITOR_ATTR = "data-nx-copy-editor";
export { COPY_EDIT_CLASS } from "@/lib/copyEditModeClass";
export const COPY_OVERRIDES_STORAGE_KEY = "nexus.copyOverrides.v1";
/** Window event fired after every store change (the editor's list re-reads). */
export const COPY_OVERRIDES_CHANGED_EVENT = "nexus:copyOverridesChanged";

const SECTION_SELECTOR =
  "section, article, li, fieldset, [data-dotrail-dot], .v2-neon-card-soft, .surface-elev";
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "TEXTAREA", "INPUT", "SELECT", "OPTION", "NOSCRIPT", "TITLE"]);

/** Whitespace-collapsed, trimmed. Shared contract with the Worker. */
export function normalizeCopyText(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

/** "/pokemon/25?x=1" -> "/pokemon/:id"; "*" stays "*". */
export function routeKeyFromPath(path: string): string {
  const trimmed = path.trim();
  if (trimmed === "*") return "*";
  const clean = trimmed.replace(/^#/, "").split("?")[0].split("#")[0];
  const withSlash = clean.startsWith("/") ? clean : `/${clean}`;
  const collapsed = withSlash
    .split("/")
    .map((seg) => (/^\d+$/.test(seg) ? ":id" : seg))
    .join("/");
  return collapsed.length > 1 ? collapsed.replace(/\/+$/, "") : collapsed;
}

/** The route key for the page on screen (hash route, or the real path for
 *  path-routed admin/auth pages). */
export function currentRouteKey(): string {
  if (typeof window === "undefined") return "/";
  const hash = window.location.hash;
  if (hash && hash.length > 1) return routeKeyFromPath(hash.slice(1));
  return routeKeyFromPath(window.location.pathname || "/");
}

/* ------------------------------------------------------------------ store -- */

const byRoute = new Map<string, Map<string, CopyOverride>>();

function routeMap(route: string): Map<string, CopyOverride> {
  let m = byRoute.get(route);
  if (!m) {
    m = new Map();
    byRoute.set(route, m);
  }
  return m;
}

export function lookupCopyOverride(route: string, original: string): CopyOverride | undefined {
  const key = normalizeCopyText(original);
  return byRoute.get(route)?.get(key) ?? byRoute.get("*")?.get(key);
}

export function listCopyOverrides(route?: string): CopyOverride[] {
  const out: CopyOverride[] = [];
  for (const [r, m] of byRoute) {
    if (route && r !== route && r !== "*") continue;
    for (const o of m.values()) out.push(o);
  }
  return out;
}

function hasOverridesFor(route: string): boolean {
  return (byRoute.get(route)?.size ?? 0) > 0 || (byRoute.get("*")?.size ?? 0) > 0;
}

function emitChanged(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(COPY_OVERRIDES_CHANGED_EVENT));
}

function persist(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      COPY_OVERRIDES_STORAGE_KEY,
      JSON.stringify({ savedAt: Date.now(), overrides: listCopyOverrides() }),
    );
  } catch {
    /* quota / private mode: the next fetch restores it */
  }
}

function sanitize(o: unknown): CopyOverride | null {
  if (!o || typeof o !== "object") return null;
  const r = o as Record<string, unknown>;
  if (typeof r.route !== "string" || typeof r.original !== "string") return null;
  const action: CopyAction =
    r.action === "hide" || r.action === "hide_section" ? r.action : "replace";
  const original = normalizeCopyText(r.original);
  if (!original) return null;
  const value = action === "replace" ? (typeof r.value === "string" ? r.value : "") : null;
  if (action === "replace" && !value) return null;
  return { route: routeKeyFromPath(r.route), original, action, value };
}

/** Replace the whole set (the launch fetch). Re-applies to the page. */
export function setCopyOverrides(list: unknown[], opts: { persist?: boolean } = {}): void {
  byRoute.clear();
  for (const raw of list) {
    const o = sanitize(raw);
    if (o) routeMap(o.route).set(o.original, o);
  }
  if (opts.persist !== false) persist();
  applyCopyOverrides();
  emitChanged();
}

export function upsertCopyOverride(raw: CopyOverride): void {
  const o = sanitize(raw);
  if (!o) return;
  routeMap(o.route).set(o.original, o);
  persist();
  applyCopyOverrides();
  emitChanged();
}

export function removeCopyOverride(route: string, original: string): void {
  byRoute.get(routeKeyFromPath(route))?.delete(normalizeCopyText(original));
  persist();
  applyCopyOverrides();
  emitChanged();
}

/** Cold start: whatever the last fetch left behind, before the network answers. */
export function loadCachedCopyOverrides(): boolean {
  if (typeof localStorage === "undefined") return false;
  try {
    const raw = localStorage.getItem(COPY_OVERRIDES_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { overrides?: unknown[] };
    if (!Array.isArray(parsed.overrides)) return false;
    setCopyOverrides(parsed.overrides, { persist: false });
    return true;
  } catch {
    return false;
  }
}

/* ----------------------------------------------------------------- engine -- */

interface AppliedRecord {
  /** What React wrote (the bundled copy). */
  original: string;
  /** What we wrote over it. */
  applied: string;
}

const applied = new WeakMap<Text, AppliedRecord>();
const hiddenEls = new Map<Element, string>();
let currentRoute = "/";
let observer: MutationObserver | null = null;
let installed = false;
/** True once anything was ever swapped; a page with no overrides still needs
 *  one restore pass after the last override is deleted. */
let dirty = false;

function isSkipped(parent: Element): boolean {
  if (SKIP_TAGS.has(parent.tagName)) return true;
  if (parent.closest(`[${COPY_EDITOR_ATTR}]`)) return true;
  return false;
}

/** The bundled text behind a node (after our swap, or as rendered). */
export function originalTextOf(t: Text): string {
  const rec = applied.get(t);
  const cur = t.nodeValue ?? "";
  return rec && cur === rec.applied ? rec.original : cur;
}

function withEdges(original: string, value: string): string {
  const lead = /^\s*/.exec(original)?.[0] ?? "";
  const trail = /\s*$/.exec(original)?.[0] ?? "";
  return `${lead}${value}${trail}`;
}

function applyTextNode(t: Text): void {
  const parent = t.parentElement;
  if (!parent || isSkipped(parent)) return;
  const cur = t.nodeValue ?? "";
  const rec = applied.get(t);
  let original: string;
  if (rec && cur === rec.applied) original = rec.original;
  else {
    // Either untouched, or React wrote a new value over our swap: the DOM
    // text is the truth again.
    original = cur;
    if (rec) applied.delete(t);
  }
  const key = normalizeCopyText(original);
  if (!key) return;
  const ov = lookupCopyOverride(currentRoute, key);
  if (!ov) {
    if (rec && cur === rec.applied) {
      t.nodeValue = rec.original;
      applied.delete(t);
    }
    return;
  }
  if (ov.action === "replace") {
    const next = withEdges(original, ov.value ?? "");
    if (cur !== next) t.nodeValue = next;
    applied.set(t, { original, applied: next });
    dirty = true;
    return;
  }
  // hide / hide_section: keep the text as bundled, hide the block around it.
  if (rec && cur === rec.applied) {
    t.nodeValue = rec.original;
    applied.delete(t);
  }
  const target =
    ov.action === "hide" ? parent : (parent.closest(SECTION_SELECTOR) as Element | null) ?? parent;
  if (target.getAttribute(COPY_HIDDEN_ATTR) !== ov.action) target.setAttribute(COPY_HIDDEN_ATTR, ov.action);
  hiddenEls.set(target, key);
  dirty = true;
}

function walkTextNodes(root: Node, fn: (t: Text) => void): void {
  if (root.nodeType === Node.TEXT_NODE) {
    fn(root as Text);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;
  const doc = root.ownerDocument ?? (root as Document);
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const batch: Text[] = [];
  let n = walker.nextNode();
  while (n) {
    batch.push(n as Text);
    n = walker.nextNode();
  }
  // Collected first: swapping a node's value while walking is fine, but
  // hiding re-parents nothing, so a plain loop is safe either way.
  for (const t of batch) fn(t);
}

function reconcileHidden(): void {
  for (const [el, key] of hiddenEls) {
    if (!el.isConnected) {
      hiddenEls.delete(el);
      continue;
    }
    const ov = lookupCopyOverride(currentRoute, key);
    const want = ov && ov.action !== "replace" ? ov.action : null;
    if (!want) {
      el.removeAttribute(COPY_HIDDEN_ATTR);
      hiddenEls.delete(el);
    } else if (el.getAttribute(COPY_HIDDEN_ATTR) !== want) {
      el.setAttribute(COPY_HIDDEN_ATTR, want);
    }
  }
}

/** One full pass over the page for the current route. */
export function applyCopyOverrides(root?: Node): void {
  if (typeof document === "undefined") return;
  currentRoute = currentRouteKey();
  const scope = root ?? document.body;
  if (!scope) return;
  if (!hasOverridesFor(currentRoute) && !dirty) return;
  reconcileHidden();
  walkTextNodes(scope, applyTextNode);
  if (!hasOverridesFor(currentRoute) && hiddenEls.size === 0) dirty = false;
}

function onMutations(records: MutationRecord[]): void {
  if (!hasOverridesFor(currentRoute) && !dirty) return;
  for (const r of records) {
    if (r.type === "characterData") {
      if (r.target.nodeType === Node.TEXT_NODE) applyTextNode(r.target as Text);
      continue;
    }
    r.addedNodes.forEach((n) => walkTextNodes(n, applyTextNode));
  }
}

function onRouteChange(): void {
  const next = currentRouteKey();
  if (next === currentRoute && !dirty) return;
  currentRoute = next;
  // The new page renders after the hashchange handlers; the observer catches
  // its nodes with the new route already set. This pass covers anything that
  // stayed mounted across the navigation (the shell, the tab bar).
  applyCopyOverrides();
}

/** Install the observer + route listener once. Safe to call repeatedly. */
export function initCopyOverrides(): void {
  if (installed || typeof window === "undefined" || typeof MutationObserver === "undefined") return;
  installed = true;
  currentRoute = currentRouteKey();
  loadCachedCopyOverrides();
  const start = () => {
    if (!document.body) return;
    observer = new MutationObserver(onMutations);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    applyCopyOverrides();
  };
  if (document.body) start();
  else document.addEventListener("DOMContentLoaded", start, { once: true });
  window.addEventListener("hashchange", onRouteChange);
  window.addEventListener("popstate", onRouteChange);
}

/** Tests only: drop every override, swap and listener. */
export function __resetCopyOverridesForTests(): void {
  observer?.disconnect();
  observer = null;
  installed = false;
  dirty = false;
  byRoute.clear();
  for (const el of hiddenEls.keys()) el.removeAttribute(COPY_HIDDEN_ATTR);
  hiddenEls.clear();
  if (typeof window !== "undefined") {
    window.removeEventListener("hashchange", onRouteChange);
    window.removeEventListener("popstate", onRouteChange);
  }
}

/* ------------------------------------------------------------ edit helpers -- */

export interface EditableText {
  /** The element whose direct text nodes are being edited. */
  element: Element;
  /** Its direct, non-empty text nodes (each becomes its own override). */
  nodes: Text[];
}

function directTextNodes(el: Element): Text[] {
  const out: Text[] = [];
  el.childNodes.forEach((n) => {
    if (n.nodeType === Node.TEXT_NODE && normalizeCopyText(originalTextOf(n as Text))) out.push(n as Text);
  });
  return out;
}

/**
 * The text under a tap. Walks UP from the hit element to the first ancestor
 * with its own text; failing that, DOWN to the first descendant that has some
 * (a button whose label sits in a span). Null over pure chrome (icons, art).
 */
export function findEditableTextAt(x: number, y: number): EditableText | null {
  if (typeof document === "undefined") return null;
  const hit = document.elementFromPoint(x, y);
  if (!hit) return null;
  if (hit.closest(`[${COPY_EDITOR_ATTR}]`)) return null;
  let el: Element | null = hit;
  for (let depth = 0; el && depth < 6; depth++) {
    if (!isSkipped(el)) {
      const nodes = directTextNodes(el);
      if (nodes.length) return { element: el, nodes };
    }
    el = el.parentElement;
  }
  const walker = document.createTreeWalker(hit, NodeFilter.SHOW_TEXT);
  let n = walker.nextNode();
  while (n) {
    const t = n as Text;
    const parent = t.parentElement;
    if (parent && !isSkipped(parent) && normalizeCopyText(originalTextOf(t))) {
      return { element: parent, nodes: directTextNodes(parent) };
    }
    n = walker.nextNode();
  }
  return null;
}

/** Label for the editor: what the section-hide would take with it. */
export function sectionFor(el: Element): Element {
  return (el.closest(SECTION_SELECTOR) as Element | null) ?? el;
}
